import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '../rootReducer'
import { EServerApplication } from '@/src/constants/constants'

export type TRoomStatus = 'prejoin' | 'loading' | 'rejected' | 'fulfilled'

export interface IRoomState {
  name: string | null
  livekit_url: string | null

  roomType: keyof typeof EServerApplication | null

  status: TRoomStatus
  statusCode: number

  project: string | null
  v: string | null

  phone: string
  referrer: string | null

  prefilledUsername: string | null
  hiddenButtons: {
    screenShare: boolean
    chat: boolean
    leave: boolean
  }
}

export const initialRoomState: IRoomState = {
  name: null,
  livekit_url: null,

  roomType: null,

  status: 'prejoin',
  statusCode: 200,

  project: null,
  v: null,

  phone: '+7',
  referrer: null,

  prefilledUsername: null,
  hiddenButtons: {
    screenShare: false,
    chat: false,
    leave: false,
  },
}

const roomAdapter = createEntityAdapter()

export const roomSlice = createSlice({
  name: 'room',
  initialState: roomAdapter.getInitialState(initialRoomState),
  reducers: {
    resetRoomState: (state) => ({ ...state, ...initialRoomState }),
    setRoomState: (state, action: PayloadAction<IRoomState>) => ({ ...state, ...action.payload }),
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload
    },
    setLivekitUrl: (state, action: PayloadAction<string>) => {
      state.livekit_url = action.payload
    },
    setRoomType: (state, action: PayloadAction<IRoomState['roomType']>) => {
      state.roomType = action.payload
    },
    setStatus: (state, action: PayloadAction<TRoomStatus>) => {
      state.status = action.payload
    },
    setStatusCode: (state, action: PayloadAction<number>) => {
      state.statusCode = action.payload
    },
    setProject: (state, action: PayloadAction<string | null>) => {
      state.project = action.payload
    },
    setV: (state, action: PayloadAction<string | null>) => {
      state.v = action.payload
    },
    setPhone: (state, action: PayloadAction<string>) => {
      state.phone = action.payload
    },
    setReferrer: (state, action: PayloadAction<string | null>) => {
      state.referrer = action.payload
    },
    setPrefilledUsername: (state, action: PayloadAction<string | null>) => {
      state.prefilledUsername = action.payload
    },
    setHiddenButtons: (state, action: PayloadAction<Partial<IRoomState['hiddenButtons']>>) => {
      state.hiddenButtons = { ...state.hiddenButtons, ...action.payload }
    },
  },
})

export const {
  resetRoomState,
  setRoomState,
  setName,
  setLivekitUrl,
  setRoomType,
  setStatus,
  setStatusCode,
  setProject,
  setV,
  setPhone,
  setReferrer,
  setPrefilledUsername,
  setHiddenButtons,
} = roomSlice.actions

export const { selectAll: roomSelector } = roomAdapter.getSelectors(
  (state: RootState) => state[roomSlice.name],
)

export const selectRoomState = (state: RootState) => state.room
export const selectRoomType = (state: RootState) => state.room.roomType
export const selectRoomStatus = (state: RootState) => state.room.status
export const selectRoomStatusCode = (state: RootState) => state.room.statusCode
export const selectProject = (state: RootState) => state.room.project
export const selectV = (state: RootState) => state.room.v
export const selectPhone = (state: RootState) => state.room.phone
export const selectReferrer = (state: RootState) => state.room.referrer
export const selectPrefilledUsername = (state: RootState) => state.room.prefilledUsername
export const selectHiddenButtons = (state: RootState) => state.room.hiddenButtons
